import React, { useState, useEffect } from 'react';
import { paymentService } from '../../services/paymentService';
import './PSEPayment.css';

const CreditCardPayment = ({ amount, displayAmount, products = [], shippingData = null, couponCode = '', guestInfo = null, onSuccess, onError, onCancel }) => {
  const shownAmount = displayAmount ?? amount;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardExpMonth: '',
    cardExpYear: '',
    cardCvc: '',
    docType: 'CC',
    docNumber: '',
    name: '',
    lastName: '',
    email: '',
    phone: '',
    dues: '1',
  });
  const [errors, setErrors] = useState({});

  const documentTypes = paymentService.getDocumentTypes();

  // Pre-fill guest info when provided
  useEffect(() => {
    if (guestInfo) {
      const parts = (guestInfo.nombre || '').trim().split(' ');
      setFormData(prev => ({
        ...prev,
        name: parts[0] || '',
        lastName: parts.slice(1).join(' ') || '',
        email: guestInfo.email || '',
        phone: shippingData?.telefono_contacto || prev.phone,
      }));
    }
  }, [guestInfo, shippingData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'cardNumber') {
      const cleaned = value.replace(/\D/g, '').slice(0, 16);
      setFormData(prev => ({ ...prev, cardNumber: cleaned }));
    } else if (name === 'cardCvc') {
      const cleaned = value.replace(/\D/g, '').slice(0, 4);
      setFormData(prev => ({ ...prev, cardCvc: cleaned }));
    } else if (name === 'cardExpMonth') {
      const cleaned = value.replace(/\D/g, '').slice(0, 2);
      setFormData(prev => ({ ...prev, cardExpMonth: cleaned }));
    } else if (name === 'cardExpYear') {
      const cleaned = value.replace(/\D/g, '').slice(0, 4);
      setFormData(prev => ({ ...prev, cardExpYear: cleaned }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const getCardType = () => {
    if (!formData.cardNumber) return null;
    return paymentService.getCreditCardType(formData.cardNumber);
  };

  const getCardIcon = () => {
    const type = getCardType();
    switch (type) {
      case 'visa': return '💳 Visa';
      case 'mastercard': return '💳 Mastercard';
      case 'amex': return '💳 Amex';
      case 'discover': return '💳 Discover';
      default: return formData.cardNumber.length > 0 ? '💳' : '';
    }
  };

  const formatDisplayCard = () => {
    if (!formData.cardNumber) return '';
    return paymentService.formatCreditCard(formData.cardNumber);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.cardNumber) {
      newErrors.cardNumber = 'Número de tarjeta es requerido';
    } else if (!paymentService.validateCreditCard(formData.cardNumber)) {
      newErrors.cardNumber = 'Número de tarjeta no es válido';
    }

    if (!formData.cardExpMonth || !formData.cardExpYear) {
      newErrors.cardExp = 'Fecha de vencimiento es requerida';
    } else {
      const month = parseInt(formData.cardExpMonth, 10);
      const year = parseInt(formData.cardExpYear, 10);
      if (month < 1 || month > 12) {
        newErrors.cardExp = 'Mes no válido (01-12)';
      } else {
        const now = new Date();
        const expDate = new Date(year, month);
        if (expDate <= now) {
          newErrors.cardExp = 'La tarjeta está vencida';
        }
      }
    }

    if (!formData.cardCvc) {
      newErrors.cardCvc = 'CVC es requerido';
    } else if (formData.cardCvc.length < 3) {
      newErrors.cardCvc = 'CVC debe tener al menos 3 dígitos';
    }

    if (!formData.docNumber) {
      newErrors.docNumber = 'Número de documento es requerido';
    } else if (formData.docNumber.length < 7) {
      newErrors.docNumber = 'Número de documento debe tener al menos 7 dígitos';
    }

    if (!formData.name) {
      newErrors.name = 'Nombre es requerido';
    }

    if (!formData.email) {
      newErrors.email = 'Email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email no es válido';
    }

    if (!formData.phone) {
      newErrors.phone = 'Teléfono es requerido';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Teléfono debe tener 10 dígitos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const paymentData = {
        amount: parseFloat(amount),
        description: 'Compra en Tablero Infinito',
        payment_method: 'credit_card',
        name: formData.name,
        last_name: formData.lastName || '',
        email: formData.email,
        phone: formData.phone,
        doc_type: formData.docType,
        doc_number: formData.docNumber,
        card_number: formData.cardNumber,
        card_exp_year: formData.cardExpYear,
        card_exp_month: formData.cardExpMonth,
        card_cvc: formData.cardCvc,
        items: products
          .filter(p => p.productId)
          .map(p => ({
            producto_id: parseInt(p.productId),
            nombre: p.name,
            cantidad: p.quantity,
            precio: p.price,
          })),
        ...(shippingData || {}),
        ...(couponCode ? { coupon_code: couponCode } : {}),
      };

      const response = await paymentService.createPayment(paymentData);
      onSuccess?.(response);
    } catch (error) {
      console.error('Error creating credit card payment:', error);
      if (error.message === 'canceled' || error.code === 'ERR_NETWORK') return;
      onError?.(error.error || error.message || 'Error procesando el pago con tarjeta');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 12 }, (_, i) => currentYear + i);

  return (
    <div className="pse-payment-container">
      <div className="pse-header">
        <div className="pse-logo">
          <span className="bank-icon">💳</span>
          <h3>Pago con Tarjeta de Crédito</h3>
        </div>
        <div className="amount-display">
          <span className="amount-label">Total a pagar:</span>
          <span className="amount-value">{formatCurrency(shownAmount)}</span>
        </div>
      </div>

      <div className="pse-info">
        <div className="info-item">
          <span className="info-icon">🔒</span>
          <span>Datos encriptados con SSL</span>
        </div>
        <div className="info-item">
          <span className="info-icon">💳</span>
          <span>Visa, Mastercard, American Express</span>
        </div>
        <div className="info-item">
          <span className="info-icon">🛡️</span>
          <span>Procesado por ePayco (PCI DSS)</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="pse-form">
        {/* Card Number */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="cardNumber">Número de Tarjeta *</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                id="cardNumber"
                name="cardNumber"
                value={formatDisplayCard()}
                onChange={handleInputChange}
                className={errors.cardNumber ? 'error' : ''}
                placeholder="1234 5678 9012 3456"
                disabled={loading}
                maxLength={19}
                inputMode="numeric"
              />
              {getCardIcon() && (
                <span style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '14px',
                  color: 'var(--color-accent-light)',
                  fontWeight: '600',
                }}>
                  {getCardIcon()}
                </span>
              )}
            </div>
            {errors.cardNumber && <span className="error-text">{errors.cardNumber}</span>}
          </div>
        </div>

        {/* Expiration & CVC */}
        <div className="form-row">
          <div className="form-group">
            <label>Fecha de Vencimiento *</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <select
                name="cardExpMonth"
                value={formData.cardExpMonth}
                onChange={handleInputChange}
                className={errors.cardExp ? 'error' : ''}
                disabled={loading}
                style={{ flex: 1 }}
              >
                <option value="">Mes</option>
                {Array.from({ length: 12 }, (_, i) => {
                  const m = String(i + 1).padStart(2, '0');
                  return <option key={m} value={m}>{m}</option>;
                })}
              </select>
              <select
                name="cardExpYear"
                value={formData.cardExpYear}
                onChange={handleInputChange}
                className={errors.cardExp ? 'error' : ''}
                disabled={loading}
                style={{ flex: 1 }}
              >
                <option value="">Año</option>
                {years.map(y => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>
            {errors.cardExp && <span className="error-text">{errors.cardExp}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="cardCvc">CVC / CVV *</label>
            <input
              type="password"
              id="cardCvc"
              name="cardCvc"
              value={formData.cardCvc}
              onChange={handleInputChange}
              className={errors.cardCvc ? 'error' : ''}
              placeholder="123"
              disabled={loading}
              maxLength={4}
              inputMode="numeric"
              autoComplete="cc-csc"
            />
            {errors.cardCvc && <span className="error-text">{errors.cardCvc}</span>}
          </div>
        </div>

        <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '16px 0' }} />

        {/* Document */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="docType">Tipo de Documento</label>
            <select
              id="docType"
              name="docType"
              value={formData.docType}
              onChange={handleInputChange}
              disabled={loading}
            >
              {documentTypes.map((type) => (
                <option key={type.code} value={type.code}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="docNumber">Número de Documento *</label>
            <input
              type="text"
              id="docNumber"
              name="docNumber"
              value={formData.docNumber}
              onChange={handleInputChange}
              className={errors.docNumber ? 'error' : ''}
              placeholder="Ej: 12345678"
              disabled={loading}
            />
            {errors.docNumber && <span className="error-text">{errors.docNumber}</span>}
          </div>
        </div>

        {/* Name */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Nombres *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? 'error' : ''}
              placeholder="Nombres"
              disabled={loading}
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Apellidos</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder="Apellidos"
              disabled={loading}
            />
          </div>
        </div>

        {/* Contact */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              placeholder="ejemplo@email.com"
              disabled={loading}
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="phone">Teléfono *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className={errors.phone ? 'error' : ''}
              placeholder="3001234567"
              disabled={loading}
            />
            {errors.phone && <span className="error-text">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="dues">Cuotas</label>
            <select
              id="dues"
              name="dues"
              value={formData.dues}
              onChange={handleInputChange}
              disabled={loading}
            >
              {[1, 2, 3, 6, 12, 24, 36].map(n => (
                <option key={n} value={String(n)}>
                  {n === 1 ? '1 cuota (sin interés)' : `${n} cuotas`}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !formData.cardNumber}
          >
            {loading ? (
              <>
                <span className="spinner-small"></span>
                Procesando...
              </>
            ) : (
              <>
                <span className="payment-icon">🔒</span>
                Pagar {formatCurrency(shownAmount)}
              </>
            )}
          </button>
        </div>
      </form>

      <div className="pse-footer">
        <p className="security-note">
          🔒 Tus datos de tarjeta son enviados directamente a ePayco con encriptación SSL de 256 bits
        </p>
        <p className="process-note">
          ℹ️ No almacenamos los datos de tu tarjeta en nuestros servidores
        </p>
      </div>
    </div>
  );
};

export default CreditCardPayment;
